import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseclient";

const T = {
  bg: "#06090f", panel: "#0b0f1a", card: "#0f1623",
  border: "#1a2540", gold: "#c9a96e", goldLight: "#e8c98a",
  text: "#e8eaf0", textMid: "#7a8599", textDim: "#3d4a5c",
  active: "#10b981", activeText: "#34d399", moderate: "#fbbf24",
};

const PAYMENT_MODES = ["Cash", "UPI", "Card", "Cheque", "Bank Transfer"];

function SectionLabel({ text }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700,
      letterSpacing: "0.9px",
      textTransform: "uppercase",
      color: "#7a6640", marginBottom: 13,
    }}>
      {text}
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <label style={{
      display: "block", fontSize: 11,
      fontWeight: 600, letterSpacing: "0.7px",
      textTransform: "uppercase",
      color: T.textDim, marginBottom: 5,
    }}>
      {children}
    </label>
  );
}

export default function BillingTab({ 
  patient, 
  session, 
  clinicSettings,
  notify 
}) {
  const [payments, setPayments]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [form, setForm] = useState({
    amount: "",
    payment_mode: "Cash",
    payment_date: new Date().toISOString().split("T")[0],
    note: "",
  });

  // ── Fetch payments ───────────────────────────────
  async function fetchPayments() {
    if (!patient?.id || !session?.user?.id) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("billing_payments")
      .select("*")
      .eq("patient_id", patient.id)
      .eq("user_id", session.user.id)
      .order("payment_date", { ascending: false });

    if (error) {
      notify("Failed to load payments: " + error.message, "error");
    } else {
      setPayments(data || []);
    }
    setLoading(false);
  }

  useEffect(() => { fetchPayments(); }, [patient?.id]);

  // ── Add payment ──────────────────────────────────
  async function handleAddPayment() {
    if (!form.amount || Number(form.amount) <= 0) {
      notify("Please enter a valid amount", "error");
      return;
    }
    setSaving(true);

    // Generate invoice number
    const invoiceNum = `INV-${Date.now()}`;

    const { error } = await supabase
      .from("billing_payments")
      .insert({
        patient_id:    patient.id,
        user_id:       session.user.id,
        amount:        Number(form.amount),
        payment_mode:  form.payment_mode,
        payment_date:  form.payment_date,
        note:          form.note,
        invoice_number: invoiceNum,
      });

    if (error) {
      notify("Failed to save payment: " + error.message, "error");
      setSaving(false);
      return;
    }

    notify("Payment recorded ✓");
    setForm({
      amount: "",
      payment_mode: "Cash",
      payment_date: new Date().toISOString().split("T")[0],
      note: "",
    });
    setShowAddForm(false);
    setSaving(false);
    fetchPayments();
  }

  // ── Delete payment ───────────────────────────────
  async function handleDelete(id) {
    if (!window.confirm("Delete this payment record?")) return;
    const { error } = await supabase
      .from("billing_payments")
      .delete()
      .eq("id", id);

    if (error) {
      notify("Delete failed: " + error.message, "error");
      return;
    }
    notify("Payment deleted");
    fetchPayments();
  }

  // ── Calculations ─────────────────────────────────
  const packageAmount  = Number(patient?.amount) || 0;
  const totalPaid      = payments.reduce(
    (sum, p) => sum + Number(p.amount), 0
  );
  const balance        = packageAmount - totalPaid;
  const sessionsLeft   = patient?.totalSessions - patient?.sessions;

  // ── Generate Invoice PDF ─────────────────────────
  function generateInvoice(payment = null) {
    const invoicePayments = payment ? [payment] : payments;
    const isFullInvoice   = !payment;

    const rows = invoicePayments.map((p, i) => `
      <tr style="border-bottom: 1px solid #eee;">
        <td style="padding: 10px 8px;">${i + 1}</td>
        <td style="padding: 10px 8px;">
          Physiotherapy Treatment
          ${p.note ? `<br/><small style="color:#666">
            ${p.note}</small>` : ""}
        </td>
        <td style="padding: 10px 8px; text-align:center;">
          ${p.payment_mode}
        </td>
        <td style="padding: 10px 8px; text-align:center;">
          ${new Date(p.payment_date)
            .toLocaleDateString("en-IN")}
        </td>
        <td style="padding: 10px 8px; text-align:right; 
                   font-weight:600;">
          ₹${Number(p.amount).toLocaleString("en-IN")}
        </td>
      </tr>
    `).join("");

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${patient?.name}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { 
            font-family: Arial, sans-serif; 
            color: #111;
            padding: 0;
            background: #fff;
          }
          .page {
            max-width: 800px;
            margin: 0 auto;
            padding: 40px;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 3px solid #c9a96e;
          }
          .clinic-name {
            font-size: 22px;
            font-weight: 700;
            color: #1a1a2e;
            margin-bottom: 4px;
          }
          .clinic-info {
            font-size: 12px;
            color: #555;
            line-height: 1.7;
          }
          .invoice-title {
            text-align: right;
          }
          .invoice-title h1 {
            font-size: 28px;
            color: #c9a96e;
            font-weight: 700;
            letter-spacing: 2px;
          }
          .invoice-meta {
            font-size: 12px;
            color: #555;
            margin-top: 4px;
            line-height: 1.7;
          }
          .bill-to {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 28px;
            padding: 16px;
            background: #f9f9f9;
            border-radius: 8px;
            border-left: 4px solid #c9a96e;
          }
          .bill-section-title {
            font-size: 10px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #999;
            margin-bottom: 6px;
          }
          .bill-name {
            font-size: 15px;
            font-weight: 700;
            color: #111;
            margin-bottom: 3px;
          }
          .bill-detail {
            font-size: 12px;
            color: #555;
            line-height: 1.6;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          thead {
            background: #1a1a2e;
            color: white;
          }
          thead th {
            padding: 12px 8px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            text-align: left;
          }
          thead th:last-child {
            text-align: right;
          }
          tbody tr:nth-child(even) {
            background: #fafafa;
          }
          td { font-size: 13px; color: #333; }
          .summary-box {
            margin-left: auto;
            width: 280px;
            border: 1px solid #eee;
            border-radius: 8px;
            overflow: hidden;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 10px 16px;
            font-size: 13px;
            border-bottom: 1px solid #eee;
          }
          .summary-row.total {
            background: #1a1a2e;
            color: white;
            font-weight: 700;
            font-size: 15px;
          }
          .summary-row.balance {
            background: ${balance > 0 
              ? "#fff3cd" : "#d4edda"};
            color: ${balance > 0 
              ? "#856404" : "#155724"};
            font-weight: 700;
          }
          .treatment-summary {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
            margin-bottom: 24px;
          }
          .treatment-box {
            background: #f9f9f9;
            border-radius: 8px;
            padding: 12px;
            text-align: center;
            border: 1px solid #eee;
          }
          .treatment-box .val {
            font-size: 20px;
            font-weight: 700;
            color: #1a1a2e;
          }
          .treatment-box .lbl {
            font-size: 10px;
            color: #999;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-top: 2px;
          }
          .footer {
            margin-top: 40px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            padding-top: 20px;
            border-top: 1px solid #eee;
          }
          .footer-note {
            font-size: 11px;
            color: #999;
            line-height: 1.7;
          }
          .signature-section {
            text-align: center;
          }
          .signature-line {
            width: 160px;
            border-top: 1px solid #333;
            margin: 8px auto 4px;
          }
          .signature-name {
            font-size: 12px;
            font-weight: 700;
            color: #333;
          }
          .signature-desig {
            font-size: 10px;
            color: #666;
          }
          .status-badge {
            display: inline-block;
            padding: 3px 12px;
            border-radius: 20px;
            font-size: 11px;
            font-weight: 700;
            background: ${balance <= 0 
              ? "#d4edda" : "#fff3cd"};
            color: ${balance <= 0 
              ? "#155724" : "#856404"};
          }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="page">

          <!-- HEADER -->
          <div class="header">
            <div>
              <div class="clinic-name">
                ${clinicSettings?.clinic_name || "Clinic"}
              </div>
              <div class="clinic-info">
                ${clinicSettings?.owner_name 
                  ? `${clinicSettings.owner_name}<br/>` 
                  : ""}
                ${clinicSettings?.address 
                  ? `${clinicSettings.address}<br/>` 
                  : ""}
                ${clinicSettings?.phone 
                  ? `📞 ${clinicSettings.phone}` 
                  : ""}
                ${clinicSettings?.email 
                  ? ` · ✉ ${clinicSettings.email}` 
                  : ""}
              </div>
            </div>
            <div class="invoice-title">
              <h1>INVOICE</h1>
              <div class="invoice-meta">
  <strong>Invoice #:</strong> 
  INV-${Date.now().toString().slice(-6)}
  <br/>
  <strong>Date:</strong> 
  ${new Date().toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })}
  <br/>
  <strong>Status:</strong>
                <span class="status-badge">
                  ${balance <= 0 ? "PAID" : "PARTIAL"}
                </span>
              </div>
            </div>
          </div>

          <!-- BILL TO -->
          <div class="bill-to">
            <div>
              <div class="bill-section-title">
                Bill To
              </div>
              <div class="bill-name">${patient?.name}</div>
              <div class="bill-detail">
                ${patient?.age ? `Age: ${patient.age} yrs` : ""}
                ${patient?.gender 
                  ? ` · ${patient.gender}` : ""}<br/>
                ${patient?.phone 
                  ? `📞 ${patient.phone}` : ""}
                ${patient?.address 
                  ? `<br/>📍 ${patient.address}` : ""}
              </div>
            </div>
            <div>
              <div class="bill-section-title">
                Treatment Info
              </div>
              <div class="bill-detail">
                <strong>Diagnosis:</strong><br/>
                ${patient?.condition === "Other" 
                  ? patient?.customCondition 
                  : patient?.condition}<br/>
                <strong>Therapist:</strong> 
                ${patient?.therapist}<br/>
                <strong>Join Date:</strong> 
                ${patient?.joinDate}
              </div>
            </div>
          </div>

          <!-- TREATMENT SUMMARY -->
          <div class="treatment-summary">
            <div class="treatment-box">
              <div class="val">
                ${patient?.totalSessions}
              </div>
              <div class="lbl">Sessions Prescribed</div>
            </div>
            <div class="treatment-box">
              <div class="val">${patient?.sessions}</div>
              <div class="lbl">Sessions Completed</div>
            </div>
            <div class="treatment-box">
              <div class="val">
                ₹${packageAmount.toLocaleString("en-IN")}
              </div>
              <div class="lbl">Package Amount</div>
            </div>
            <div class="treatment-box">
              <div class="val" style="color:${
                balance > 0 ? "#856404" : "#155724"
              }">
                ₹${Math.abs(balance).toLocaleString("en-IN")}
              </div>
              <div class="lbl">
                ${balance > 0 ? "Balance Due" : "Fully Paid"}
              </div>
            </div>
          </div>

          <!-- PAYMENTS TABLE -->
          <table>
            <thead>
              <tr>
                <th style="width:40px">#</th>
                <th>Description</th>
                <th style="text-align:center">Mode</th>
                <th style="text-align:center">Date</th>
                <th style="text-align:right">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>

          <!-- SUMMARY BOX -->
          <div style="display:flex; 
                      justify-content:flex-end; 
                      margin-bottom:32px;">
            <div class="summary-box">
              <div class="summary-row">
                <span>Package Amount</span>
                <span>
                  ₹${packageAmount.toLocaleString("en-IN")}
                </span>
              </div>
              <div class="summary-row">
                <span>Total Paid</span>
                <span style="color:#155724; font-weight:600;">
                  ₹${totalPaid.toLocaleString("en-IN")}
                </span>
              </div>
              <div class="summary-row balance">
                <span>
                  ${balance > 0 
                    ? "Balance Due" 
                    : "Overpaid / Advance"}
                </span>
                <span>
                  ₹${Math.abs(balance).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </div>

          <!-- FOOTER -->
          <div class="footer">
            <div class="footer-note">
              Thank you for choosing 
              ${clinicSettings?.clinic_name || "our clinic"}!
              <br/>
              For queries: 
              ${clinicSettings?.phone || ""} · 
              ${clinicSettings?.email || ""}
              <br/>
              <em>
                This is a computer generated invoice.
              </em>
            </div>
            <div class="signature-section">
              <div style="height:40px;"></div>
              <div class="signature-line"></div>
              <div class="signature-name">
                ${clinicSettings?.signature_name 
                  || clinicSettings?.owner_name 
                  || ""}
              </div>
              <div class="signature-desig">
                ${clinicSettings?.designation || ""}
              </div>
            </div>
          </div>

        </div>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
  }

  // ── WhatsApp billing summary ─────────────────────
  function sendWhatsAppSummary() {
    const phone = (patient?.phone || "")
      .replace(/\D/g, "");
    if (!phone) {
      notify("Patient phone number missing", "error");
      return;
    }

    const msg = encodeURIComponent(
      `💊 *Billing Summary*\n` +
      `*${clinicSettings?.clinic_name || "Clinic"}*\n\n` +
      `Patient: ${patient?.name}\n` +
      `Diagnosis: ${patient?.condition}\n` +
      `Sessions: ${patient?.sessions}/` +
        `${patient?.totalSessions}\n\n` +
      `💰 *Payment Details*\n` +
      `Package Amount: ₹${packageAmount.toLocaleString("en-IN")}\n` +
      `Amount Paid: ₹${totalPaid.toLocaleString("en-IN")}\n` +
      `Balance: ₹${Math.max(balance, 0).toLocaleString("en-IN")}\n\n` +
      `Status: ${balance <= 0 ? "✅ FULLY PAID" : "⏳ PAYMENT PENDING"}\n\n` +
      `For queries: ${clinicSettings?.phone || ""}\n` +
      `— ${clinicSettings?.signature_name || clinicSettings?.owner_name || ""}`
    );

    window.open(
      `https://wa.me/${phone}?text=${msg}`, 
      "_blank"
    );
  }

  // ── Render ───────────────────────────────────────
  return (
    <div style={{ padding: "0 0 30px" }}>

      {/* ── BILLING OVERVIEW ── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        gap: 14,
        marginBottom: 24,
      }}>
        {[
          {
            label: "Package Amount",
            value: `₹${packageAmount.toLocaleString("en-IN")}`,
            color: T.gold,
          },
          {
            label: "Total Paid",
            value: `₹${totalPaid.toLocaleString("en-IN")}`,
            color: T.activeText,
          },
          {
            label: balance > 0 ? "Balance Due" : "Fully Paid",
            value: `₹${Math.abs(balance).toLocaleString("en-IN")}`,
            color: balance > 0 ? T.moderate : T.activeText,
          },
          {
            label: "Sessions Done",
            value: `${patient?.sessions}/${patient?.totalSessions}`,
            color: "#a78bfa",
          },
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              background: T.card,
              border: `1px solid ${T.border}`,
              borderRadius: 10,
              padding: "18px 16px",
            }}
          >
            <div style={{
              fontSize: 10,
              color: T.textDim,
              textTransform: "uppercase",
              letterSpacing: "0.8px",
              marginBottom: 8,
              fontWeight: 700,
            }}>
              {stat.label}
            </div>
            <div style={{
              fontSize: 24,
              fontWeight: 700,
              color: stat.color,
              fontFamily: "'Cormorant Garamond', serif",
            }}>
              {stat.value}
            </div>
          </div>
        ))}
      </div>

      {/* ── ACTION BUTTONS ── */}
      <div style={{
        display: "flex",
        gap: 10,
        marginBottom: 24,
        flexWrap: "wrap",
      }}>
        <button
          className="btn btn-gold"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          + Add Payment
        </button>
        <button
          className="btn btn-outline"
          onClick={() => generateInvoice()}
          disabled={payments.length === 0}
        >
          🖨 Print Invoice
        </button>
        <button
          className="btn btn-outline"
          onClick={sendWhatsAppSummary}
          style={{ color: "#25D366", borderColor: "#25D366" }}
        >
          <span style={{ marginRight: 6 }}>📱</span>
          WhatsApp Summary
        </button>
      </div>

      {/* ── ADD PAYMENT FORM ── */}
      {showAddForm && (
        <div style={{
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          padding: 24,
          marginBottom: 24,
        }}>
          <SectionLabel text="Record New Payment" />
          <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
            marginBottom: 16,
          }}>
            <div>
              <FieldLabel>Amount (₹) *</FieldLabel>
              <input
                type="number"
                value={form.amount}
                onChange={(e) => setForm(f => ({
                  ...f, amount: e.target.value
                }))}
                placeholder="Enter amount"
              />
            </div>
            <div>
              <FieldLabel>Payment Mode</FieldLabel>
              <select
                value={form.payment_mode}
                onChange={(e) => setForm(f => ({
                  ...f, payment_mode: e.target.value
                }))}
              >
                {PAYMENT_MODES.map(m => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel>Payment Date</FieldLabel>
              <input
                type="date"
                value={form.payment_date}
                onChange={(e) => setForm(f => ({
                  ...f, payment_date: e.target.value
                }))}
              />
            </div>
            <div>
              <FieldLabel>Note (Optional)</FieldLabel>
              <input
                value={form.note}
                onChange={(e) => setForm(f => ({
                  ...f, note: e.target.value
                }))}
                placeholder="e.g. Advance payment, Session 1-5..."
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              className="btn btn-gold"
              onClick={handleAddPayment}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Payment"}
            </button>
            <button
              className="btn btn-outline"
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── PAYMENT HISTORY ── */}
      <div style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        overflow: "hidden",
      }}>
        <div style={{
          padding: "16px 20px",
          borderBottom: `1px solid ${T.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <SectionLabel text="Payment History" />
          <span style={{
            fontSize: 11,
            color: T.textDim,
          }}>
            {payments.length} transaction
            {payments.length !== 1 ? "s" : ""}
          </span>
        </div>

        {loading ? (
          <div style={{
            padding: 32,
            textAlign: "center",
            color: T.textDim,
            fontSize: 13,
          }}>
            Loading payments...
          </div>
        ) : payments.length === 0 ? (
          <div style={{
            padding: 40,
            textAlign: "center",
          }}>
            <div style={{
              fontSize: 36,
              marginBottom: 12,
            }}>
              💳
            </div>
            <div style={{
              color: T.textDim,
              fontSize: 13,
            }}>
              No payments recorded yet.
            </div>
            <button
              className="btn btn-gold btn-sm"
              style={{ marginTop: 16 }}
              onClick={() => setShowAddForm(true)}
            >
              + Add First Payment
            </button>
          </div>
        ) : (
          <>
            {/* Table header */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr 1fr 80px",
              padding: "10px 20px",
              background: T.panel,
              borderBottom: `1px solid ${T.border}`,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "1px",
              color: T.textDim,
              textTransform: "uppercase",
            }}>
              <div>Date</div>
              <div>Amount</div>
              <div>Mode</div>
              <div>Note</div>
              <div>Actions</div>
            </div>

            {/* Payment rows */}
            {payments.map((p) => (
              <div
                key={p.id}
                className="row-hover"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr 1fr 80px",
                  padding: "14px 20px",
                  borderBottom: `1px solid ${T.border}`,
                  alignItems: "center",
                }}
              >
                <div style={{
                  fontSize: 12,
                  color: T.textMid,
                }}>
                  {new Date(p.payment_date)
                    .toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                </div>
                <div style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: T.activeText,
                  fontFamily: "'Cormorant Garamond', serif",
                }}>
                  ₹{Number(p.amount).toLocaleString("en-IN")}
                </div>
                <div>
                  <span style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "3px 10px",
                    borderRadius: 20,
                    background: "rgba(201,169,110,0.1)",
                    color: T.gold,
                  }}>
                    {p.payment_mode}
                  </span>
                </div>
                <div style={{
                  fontSize: 12,
                  color: T.textMid,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {p.note || "—"}
                </div>
                <div style={{
                  display: "flex",
                  gap: 6,
                }}>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => generateInvoice(p)}
                    title="Print this payment"
                  >
                    🖨
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(p.id)}
                    title="Delete"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}

            {/* Total row */}
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "14px 20px",
              gap: 24,
              borderTop: `2px solid ${T.border}`,
            }}>
              <span style={{
                fontSize: 12,
                color: T.textMid,
              }}>
                Total Paid:
              </span>
              <span style={{
                fontSize: 16,
                fontWeight: 700,
                color: T.activeText,
                fontFamily: "'Cormorant Garamond', serif",
              }}>
                ₹{totalPaid.toLocaleString("en-IN")}
              </span>
            </div>
          </>
        )}
      </div>

    </div>
  );
}